/**
 * SPDX-FileCopyrightText: 2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * A11y integration for custom item/passage stylesheets.
 *
 * Two layers work together:
 * 1. Load-time transforms ({@link applyA11yToCustomStyles}) — rewrite simple authored values to
 *    CSS variables with fallbacks so a11y-off preserves the original appearance.
 * 2. Runtime override sheet ({@link buildA11yContentOverrideCss}) — conditional rules keyed on
 *    data-a11y-* / data-theme attributes; backup for values not rewritten at load time.
 *
 * Contrast bridge vars ({@link applyA11yContrastVars}) are set on the test-runner container
 * only while a non-default contrast theme is active.
 */

export const A11Y_CONTENT_OVERRIDE_STYLE_ID = 'tao-a11y-custom-content-overrides';

export const A11Y_FONT_SIZE_SCALE_VAR = '--a11y-fontsize-scale';
export const A11Y_CONTRAST_COLOR_VAR = '--a11y-contrast-color';
export const A11Y_CONTRAST_BG_VAR = '--a11y-contrast-bg';
export const A11Y_CONTRAST_BORDER_VAR = '--a11y-contrast-border';

const CONTRAST_THEME_CONDITION = '[data-theme]:not([data-theme="default"])';

const CSS_RESET_KEYWORDS = new Set(['inherit', 'unset', 'initial', 'revert']);

const FONT_SIZE_KEYWORDS = new Set([
    'medium',
    'small',
    'large',
    'x-small',
    'x-large',
    'xx-small',
    'xx-large',
    'smaller',
    'larger'
]);

const LENGTH_UNIT_PATTERN = '(?:px|rem|em|%|ex|ch|vw|vh|vmin|vmax|cm|mm|in|pt|pc)';
const SIMPLE_LENGTH_PATTERN = new RegExp(`^-?[\\d.]+${LENGTH_UNIT_PATTERN}$`);
const SIMPLE_SPACING_PATTERN = /^-?[\d.]+(?:px|rem|em|%|ex|ch)$/;

const CONTENT_ELEMENTS =
    ':is(p, li, td, th, blockquote, span, div, h1, h2, h3, h4, h5, h6, label, figcaption, a, section, article)';

const INTERACTION_ELEMENTS = ':is(.qti-interaction, .qti-blockInteraction, .qti-inlineInteraction)';

const INTERACTION_DESCENDANTS_EXCLUSION = ':not(.qti-interaction *)';

const FORM_CONTROL_EXCLUSION = ':not(input, textarea, select, button, option, optgroup)';

const MEDIA_EXCLUSION = ':not(img, video, svg, canvas, audio, mjx-container, mjx-container *)';

// --- Value validators (load-time transforms) ---

function hasUnsupportedLengthFunction(value) {
    return /(?:var|clamp|min|max|calc)\s*\(/i.test(value);
}

function hasUnsupportedColorFunction(value) {
    return hasUnsupportedLengthFunction(value) || /(?:url|gradient|element)\s*\(/i.test(value);
}

function isSimpleColorValue(value) {
    const trimmed = value.trim();

    if (!trimmed || hasUnsupportedColorFunction(trimmed)) {
        return false;
    }

    const normalized = trimmed.toLowerCase();

    if (CSS_RESET_KEYWORDS.has(normalized) || ['transparent', 'currentcolor'].includes(normalized)) {
        return false;
    }

    if (/^#[\da-f]{3,8}$/i.test(trimmed)) {
        return true;
    }

    if (/^-?(?:rgb|rgba|hsl|hsla)\([^)]+\)$/i.test(trimmed)) {
        return true;
    }

    if (trimmed.includes(' ')) {
        return false;
    }

    return /^[a-z]+$/i.test(trimmed);
}

function isScalableFontSize(value) {
    const trimmed = value.trim();

    if (!trimmed || hasUnsupportedLengthFunction(trimmed) || FONT_SIZE_KEYWORDS.has(trimmed)) {
        return false;
    }

    return SIMPLE_LENGTH_PATTERN.test(trimmed);
}

function isScalableSpacing(value) {
    const trimmed = value.trim();

    if (!trimmed || hasUnsupportedLengthFunction(trimmed)) {
        return false;
    }

    return SIMPLE_SPACING_PATTERN.test(trimmed);
}

function isScalableLineHeight(value) {
    const trimmed = value.trim();

    if (!trimmed || hasUnsupportedLengthFunction(trimmed)) {
        return false;
    }

    if (/^-?[\d.]+$/.test(trimmed)) {
        return true;
    }

    return SIMPLE_SPACING_PATTERN.test(trimmed);
}

/**
 * @typedef {Object} PropertyTransformConfig
 * @property {(value: String) => boolean} canTransform
 * @property {(value: String) => String} transform
 * @property {(value: String) => boolean} isAlreadyTransformed
 */

/** @type {Record<String, PropertyTransformConfig>} */
const PROPERTY_TRANSFORMS = Object.freeze({
    'font-size': {
        canTransform: isScalableFontSize,
        transform: value => `calc(${value} * var(${A11Y_FONT_SIZE_SCALE_VAR}, 1))`,
        isAlreadyTransformed: value => value.includes(A11Y_FONT_SIZE_SCALE_VAR)
    },
    'line-height': {
        canTransform: isScalableLineHeight,
        transform: value => `var(--line-height-default, ${value})`,
        isAlreadyTransformed: value => value.includes('var(--line-height-default')
    },
    'letter-spacing': {
        canTransform: isScalableSpacing,
        transform: value => `var(--letter-spacing, ${value})`,
        isAlreadyTransformed: value => value.includes('var(--letter-spacing')
    },
    'word-spacing': {
        canTransform: isScalableSpacing,
        transform: value => `var(--word-spacing, ${value})`,
        isAlreadyTransformed: value => value.includes('var(--word-spacing')
    },
    color: {
        canTransform: isSimpleColorValue,
        transform: value => `var(${A11Y_CONTRAST_COLOR_VAR}, ${value})`,
        isAlreadyTransformed: value => value.includes(A11Y_CONTRAST_COLOR_VAR)
    },
    'background-color': {
        canTransform: isSimpleColorValue,
        transform: value => `var(${A11Y_CONTRAST_BG_VAR}, ${value})`,
        isAlreadyTransformed: value => value.includes(A11Y_CONTRAST_BG_VAR)
    },
    background: {
        canTransform: isSimpleColorValue,
        transform: value => `var(${A11Y_CONTRAST_BG_VAR}, ${value})`,
        isAlreadyTransformed: value => value.includes(A11Y_CONTRAST_BG_VAR)
    },
    'border-color': {
        canTransform: isSimpleColorValue,
        transform: value => `var(${A11Y_CONTRAST_BORDER_VAR}, ${value})`,
        isAlreadyTransformed: value => value.includes(A11Y_CONTRAST_BORDER_VAR)
    }
});

// --- Stylesheet traversal ---

/**
 * Flatten stylesheet rules, recursively including nested grouping at-rules.
 * @param {CSSRuleList} cssRules
 * @returns {CSSRule[]}
 */
function flattenStyleRules(cssRules) {
    if (!cssRules) {
        return [];
    }

    return Object.values(cssRules).flatMap(rule => {
        if (rule.selectorText) {
            return [rule];
        }

        if (rule.cssRules?.length) {
            return flattenStyleRules(rule.cssRules);
        }

        return [];
    });
}

/**
 * @param {CSSStyleDeclaration} style
 * @returns {boolean}
 */
function isStyleEditorVariablesRule(style) {
    if (!style.length) {
        return false;
    }

    for (let i = 0; i < style.length; i++) {
        if (!style[i].startsWith('--')) {
            return false;
        }
    }

    return true;
}

/**
 * @param {String} propertyName
 * @param {String} value
 * @returns {boolean}
 */
function shouldTransformProperty(propertyName, value) {
    if (!value?.trim()) {
        return false;
    }

    const trimmedValue = value.trim();

    if (CSS_RESET_KEYWORDS.has(trimmedValue)) {
        return false;
    }

    if (trimmedValue.includes('var(--a11y-') || trimmedValue.includes('var(--font-ui')) {
        return false;
    }

    if (propertyName === 'font-family') {
        return true;
    }

    const config = PROPERTY_TRANSFORMS[propertyName];
    if (!config || config.isAlreadyTransformed(trimmedValue)) {
        return false;
    }

    return config.canTransform(trimmedValue);
}

/**
 * @param {CSSStyleDeclaration} style
 * @param {String} propertyName
 * @param {String} value
 */
function transformFontFamily(style, propertyName, value) {
    const priority = style.getPropertyPriority(propertyName);
    style.setProperty('--a11y-custom-font-family', value.trim());
    style.setProperty('font-family', 'var(--font-ui, var(--a11y-custom-font-family))', priority);
}

/**
 * Rewrite authored custom stylesheet properties so a11y settings can override them through CSS variables.
 * @param {CSSRuleList} cssRules
 */
export function applyA11yToCustomStyles(cssRules) {
    if (!cssRules) {
        return;
    }

    flattenStyleRules(cssRules).forEach(rule => {
        const style = rule.style;
        if (isStyleEditorVariablesRule(style)) {
            return;
        }

        for (const propertyName of Array.from(style)) {
            const value = style.getPropertyValue(propertyName);

            if (!shouldTransformProperty(propertyName, value)) {
                continue;
            }

            if (propertyName === 'font-family') {
                transformFontFamily(style, propertyName, value);
                continue;
            }

            const config = PROPERTY_TRANSFORMS[propertyName];
            if (config) {
                const priority = style.getPropertyPriority(propertyName);
                style.setProperty(propertyName, config.transform(value.trim()), priority);
            }
        }
    });
}

// --- Contrast bridge variables (test-runner container) ---

/**
 * Apply contrast bridge variables while a non-default contrast theme is active.
 * @param {HTMLElement} target
 */
export function applyA11yContrastVars(target) {
    if (!target) {
        return;
    }

    target.style.setProperty(A11Y_CONTRAST_COLOR_VAR, 'var(--color-text-default)');
    target.style.setProperty(A11Y_CONTRAST_BG_VAR, 'var(--color-bg-default)');
    target.style.setProperty(A11Y_CONTRAST_BORDER_VAR, 'var(--color-border-default)');
}

/**
 * Remove contrast bridge variables so authored fallbacks apply again.
 * @param {HTMLElement} target
 */
export function clearA11yContrastVars(target) {
    if (!target) {
        return;
    }

    target.style.removeProperty(A11Y_CONTRAST_COLOR_VAR);
    target.style.removeProperty(A11Y_CONTRAST_BG_VAR);
    target.style.removeProperty(A11Y_CONTRAST_BORDER_VAR);
}

// --- Style Editor overrides ---

/**
 * Prefix every selector in a comma-separated list with a condition selector.
 * @param {String} conditionSelector
 * @param {String} selectorList
 * @returns {String}
 */
function prefixSelectorList(conditionSelector, selectorList) {
    return selectorList
        .split(/\s*,\s*/)
        .map(selector => `${conditionSelector} ${selector}`)
        .join(', ');
}

/**
 * @param {CSSStyleSheet} stylesheet
 * @param {String} conditionSelector
 * @param {String} scopedSelector
 * @param {String} declarations
 */
function insertConditionalRule(stylesheet, conditionSelector, scopedSelector, declarations) {
    stylesheet.insertRule(`${prefixSelectorList(conditionSelector, scopedSelector)} {${declarations}}`);
}

/**
 * Test-runner's a11y plugin should override Style Editor token variables when settings are active.
 * @param {CSSStyleSheet} newStylesheet
 * @param {String} styleEditorVarsScope
 */
export function addStyleEditorA11yOverrideRules(newStylesheet, styleEditorVarsScope) {
    insertConditionalRule(
        newStylesheet,
        '[data-a11y-override-font-family="true"]',
        styleEditorVarsScope,
        '--styleeditor-font-family: unset'
    );

    insertConditionalRule(
        newStylesheet,
        '[data-a11y-override-font-size="true"]',
        styleEditorVarsScope,
        `--styleeditor-font-size: calc(var(--styleeditor-font-size-initial) * var(${A11Y_FONT_SIZE_SCALE_VAR}, 1))`
    );

    insertConditionalRule(
        newStylesheet,
        CONTRAST_THEME_CONDITION,
        styleEditorVarsScope,
        '--styleeditor-bg-color: var(--color-gs-light);' +
            '--styleeditor-text-color: var(--color-gs-dark);' +
            '--styleeditor-border-color: var(--color-gs-dark);' +
            '--styleeditor-table-heading-bg-color: var(--color-gs-light);'
    );
}

// --- Runtime override stylesheet ---

/**
 * @param {String} scopeSelector
 * @returns {String}
 */
export function getA11yOverrideStyleId(scopeSelector) {
    return `${A11Y_CONTENT_OVERRIDE_STYLE_ID}-${scopeSelector.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

/**
 * @param {String} condition
 * @param {String} selector
 * @param {String} declarations
 * @returns {String}
 */
function buildOverrideRule(condition, selector, declarations) {
    return `${condition} ${selector} {${declarations}}`;
}

/**
 * Build conditional override rules for custom content under a scoped selector.
 * Contrast rules are a backup for properties not rewritten at load time.
 * @param {String} scopeSelector
 * @returns {String}
 */
export function buildA11yContentOverrideCss(scopeSelector) {
    const contentSelector = `${scopeSelector} ${CONTENT_ELEMENTS}${INTERACTION_DESCENDANTS_EXCLUSION}`;
    const interactionSelector = `${scopeSelector} ${INTERACTION_ELEMENTS}`;
    const customClassSelector = `${scopeSelector} [class]${FORM_CONTROL_EXCLUSION}${MEDIA_EXCLUSION}${INTERACTION_DESCENDANTS_EXCLUSION}`;
    const tableSelector = `${scopeSelector} :is(table, th, td, blockquote)`;
    const tableHeadingSelector = `${scopeSelector} :is(th):not(.borderless)`;

    const rules = [
        buildOverrideRule('[data-a11y-override-font-size="true"]', scopeSelector, 'font-size: var(--fontsize-body);'),
        buildOverrideRule('[data-a11y-override-font-size="true"]', contentSelector, 'font-size: inherit;'),
        buildOverrideRule(
            '[data-a11y-override-font-size="true"]',
            interactionSelector,
            'font-size: var(--fontsize-body);'
        ),
        buildOverrideRule('[data-a11y-override-font-family="true"]', scopeSelector, 'font-family: var(--font-ui);'),
        buildOverrideRule('[data-a11y-override-font-family="true"]', contentSelector, 'font-family: inherit;'),
        buildOverrideRule(
            '[data-a11y-override-font-family="true"]',
            interactionSelector,
            'font-family: var(--font-ui);'
        ),
        buildOverrideRule(
            '[data-a11y-override-line-height="true"]',
            contentSelector,
            'line-height: var(--line-height-default);'
        ),
        buildOverrideRule(
            '[data-a11y-override-line-height="true"]',
            interactionSelector,
            'line-height: var(--line-height-default);'
        ),
        buildOverrideRule(
            '[data-a11y-override-spacing="true"]',
            contentSelector,
            'letter-spacing: var(--letter-spacing); word-spacing: var(--word-spacing);'
        ),
        buildOverrideRule(
            '[data-a11y-override-spacing="true"]',
            interactionSelector,
            'letter-spacing: var(--letter-spacing); word-spacing: var(--word-spacing);'
        ),
        // in the following overrides, always set color and background-color together (risky to set only one)
        buildOverrideRule(
            CONTRAST_THEME_CONDITION,
            contentSelector,
            'color: var(--color-text-default); background-color: var(--color-bg-default);'
        ),
        buildOverrideRule(
            CONTRAST_THEME_CONDITION,
            interactionSelector,
            'color: var(--color-text-default);' +
                'background-color: var(--color-bg-default);' +
                'border-color: var(--color-border-default);'
        ),
        buildOverrideRule(
            CONTRAST_THEME_CONDITION,
            customClassSelector,
            'color: var(--color-text-default); background-color: var(--color-bg-default);'
        ),
        buildOverrideRule(
            CONTRAST_THEME_CONDITION,
            tableSelector,
            'color: var(--color-text-default);' +
                'background-color: var(--color-bg-default);' +
                'border-color: var(--color-border-default);'
        ),
        buildOverrideRule(
            CONTRAST_THEME_CONDITION,
            tableHeadingSelector,
            'color: var(--color-text-default); background-color: var(--color-bg-table-heading);'
        )
    ];

    return rules.join('\n');
}

/**
 * @param {ParentNode} target
 * @param {String} scopeSelector
 */
export function injectA11yContentOverrideStyle(target, scopeSelector) {
    if (!target || !scopeSelector) {
        return;
    }

    const styleId = getA11yOverrideStyleId(scopeSelector);
    const root = target.ownerDocument || document;
    if (root.getElementById(styleId)) {
        return;
    }

    const styleTag = document.createElement('style');
    styleTag.id = styleId;
    styleTag.dataset.a11yOverride = 'true';
    styleTag.textContent = buildA11yContentOverrideCss(scopeSelector);
    target.appendChild(styleTag);
}

/**
 * @param {ParentNode} target
 */
export function removeA11yContentOverrideStyles(target = document) {
    target.querySelectorAll(`style[id^="${A11Y_CONTENT_OVERRIDE_STYLE_ID}"]`).forEach(styleTag => styleTag.remove());
}
